import { useCallback, useEffect, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, CardSection, EmptyState, Field, LoadingRows, Notice, PageHeader, Panel,
  SelectField, Table, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui.jsx";

const EMPTY_SETTINGS = {
  email_from_address: "",
  email_from_name: "",
  email_reply_to_address: "",
  inbound_email_address: "",
  inbound_email_enabled: false,
  email_default_cc: "",
  email_sending_enabled: false,
  email_verification_policy: "verified_only",
  email_agent_delivery_policy: "approval_required",
  direct_send_daily_limit: 0,
};

function dateLabel(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export default function EmailSettingsPage({ embedded = false }) {
  const [state, setState] = useState({ loading: true, settings: EMPTY_SETTINGS, suppressions: [], error: "" });
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const deliveryReady = Boolean(form.email_sending_enabled && form.email_from_address && form.provider_configured);

  const load = useCallback(async () => {
    try {
      const [settingsData, suppressionData] = await Promise.all([
        api.emailSettings(),
        api.suppressions({ lifecycle: "active", limit: 100 }),
      ]);
      const settings = settingsData.email_settings;
      setState({ loading: false, settings, suppressions: suppressionData.suppressions, error: "" });
      setForm({ ...settings, email_default_cc: (settings.email_default_cc || []).join(", ") });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function update(key, value) {
    setSaved(false);
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    setState((current) => ({ ...current, error: "" }));
    try {
      const data = await api.updateEmailSettings({
        email_from_address: form.email_from_address || null,
        email_from_name: form.email_from_name || null,
        email_reply_to_address: form.email_reply_to_address || null,
        inbound_email_address: form.inbound_email_address || null,
        inbound_email_enabled: Boolean(form.inbound_email_enabled),
        email_default_cc: form.email_default_cc.split(",").map((value) => value.trim()).filter(Boolean),
        email_sending_enabled: Boolean(form.email_sending_enabled),
        email_verification_policy: form.email_verification_policy,
        email_agent_delivery_policy: form.email_agent_delivery_policy,
        direct_send_daily_limit: Number(form.direct_send_daily_limit),
      });
      setState((current) => ({ ...current, settings: data.email_settings }));
      setForm({ ...data.email_settings, email_default_cc: (data.email_settings.email_default_cc || []).join(", ") });
      setSaved(true);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id) {
    setRevoking(id);
    try {
      await api.revokeSuppression(id);
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="grid gap-8">
      {embedded ? (
        <header className="grid gap-1.5 border-b border-border pb-6">
          <h2 className="text-xl font-bold tracking-[-0.02em]">Email delivery</h2>
          <p className="max-w-[68ch] text-[13px] leading-6 text-muted-foreground">Control the organization sender, default CC recipients, delivery safeguards, approval policy, and direct-send limit.</p>
        </header>
      ) : (
        <PageHeader
          eyebrow="Administration"
          title="Email delivery"
          description="Control the workspace sender, default CC recipients, delivery safeguards, approval policy, and direct-send limit."
        />
      )}
      {state.error && <Notice>{state.error}</Notice>}
      {state.loading ? <LoadingRows count={4} /> : (
        <>
          {!form.provider_configured && (
            <Notice tone="info">
              Cloudflare delivery is not connected in this environment. You can save the sender policy now, but no email can leave RevPal until the EMAIL binding is configured.
            </Notice>
          )}
          <Panel>
            <form className="grid gap-6" onSubmit={save}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Sender email"
                  name="email_from_address"
                  type="email"
                  placeholder="outreach@yourdomain.com"
                  value={form.email_from_address || ""}
                  onChange={(event) => update("email_from_address", event.target.value)}
                />
                <Field
                  label="Sender name"
                  name="email_from_name"
                  placeholder="Your company"
                  value={form.email_from_name || ""}
                  onChange={(event) => update("email_from_name", event.target.value)}
                />
                <Field
                  label="Reply-to email"
                  hint="optional"
                  name="email_reply_to_address"
                  type="email"
                  placeholder="inbox@yourdomain.com"
                  value={form.email_reply_to_address || ""}
                  onChange={(event) => update("email_reply_to_address", event.target.value)}
                />
                <Field
                  label="Inbound reply inbox"
                  hint="Cloudflare Email Routing address"
                  name="inbound_email_address"
                  type="email"
                  placeholder="sales@yourdomain.com"
                  value={form.inbound_email_address || ""}
                  onChange={(event) => update("inbound_email_address", event.target.value)}
                />
                <Field
                  label="Default CC"
                  hint="optional, comma-separated; added to every new draft"
                  name="email_default_cc"
                  placeholder="inbox@yourdomain.com"
                  value={form.email_default_cc || ""}
                  onChange={(event) => update("email_default_cc", event.target.value)}
                />
                <SelectField
                  label="Recipient verification"
                  name="email_verification_policy"
                  value={form.email_verification_policy}
                  onChange={(event) => update("email_verification_policy", event.target.value)}
                >
                  <option value="verified_only">Verified addresses only</option>
                  <option value="allow_unverified">Allow unverified addresses</option>
                </SelectField>
                <SelectField
                  label="Agent delivery policy"
                  name="email_agent_delivery_policy"
                  value={form.email_agent_delivery_policy}
                  onChange={(event) => update("email_agent_delivery_policy", event.target.value)}
                >
                  <option value="approval_required">Always require human approval</option>
                  <option value="allow_scoped_direct">Allow scoped direct sending</option>
                </SelectField>
                <Field
                  label="Direct-send daily limit"
                  hint="0 disables direct delivery"
                  name="direct_send_daily_limit"
                  type="number"
                  min="0"
                  max="10000"
                  value={form.direct_send_daily_limit}
                  onChange={(event) => update("direct_send_daily_limit", event.target.value)}
                />
              </div>
              <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl bg-muted p-3 text-sm transition-colors hover:bg-accent">
                <input
                  className="mt-1 size-4 accent-primary"
                  name="email_sending_enabled"
                  type="checkbox"
                  checked={Boolean(form.email_sending_enabled)}
                  onChange={(event) => update("email_sending_enabled", event.target.checked)}
                />
                <span className="grid gap-1">
                  <strong className="font-semibold text-foreground">Enable email delivery</strong>
                  <small className="font-normal leading-5 text-muted-foreground">Drafting remains available while disabled, but approvals and direct sends are blocked.</small>
                </span>
              </label>
              <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl bg-muted p-3 text-sm transition-colors hover:bg-accent">
                <input
                  className="mt-1 size-4 accent-primary"
                  name="inbound_email_enabled"
                  type="checkbox"
                  checked={Boolean(form.inbound_email_enabled)}
                  onChange={(event) => update("inbound_email_enabled", event.target.checked)}
                />
                <span className="grid gap-1">
                  <strong className="font-semibold text-foreground">Capture replies in RevPal</strong>
                  <small className="font-normal leading-5 text-muted-foreground">Email routed to the inbound inbox is matched to the sender or original thread and creates a signal. Keep Reply-to set to this same inbox for outbound campaigns.</small>
                </span>
              </label>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {saved && <span className="text-xs text-green-700" role="status">Settings saved</span>}
                <Badge status={deliveryReady ? "active" : undefined}>{deliveryReady ? "Ready" : "Not ready"}</Badge>
                <Button variant="primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save email settings"}</Button>
              </div>
            </form>
          </Panel>

          <CardSection title="Suppressed recipients" meta={`${state.suppressions.length} active`}>
            {!state.suppressions.length ? (
              <EmptyState title="No suppressed recipients" description="Opt-outs, complaints, and bounced addresses will appear here and block every send path." />
            ) : (
              <Table>
                <TableHead>
                  <TableHeader>Recipient</TableHeader>
                  <TableHeader>Reason</TableHeader>
                  <TableHeader>Source</TableHeader>
                  <TableHeader>Added</TableHeader>
                  <TableHeader><span className="sr-only">Actions</span></TableHeader>
                </TableHead>
                <tbody>{state.suppressions.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell><strong className="font-semibold">{item.person_name || item.recipient_email || "Protected address"}</strong></TableCell>
                    <TableCell className="max-w-[360px] text-muted-foreground">{item.reason}</TableCell>
                    <TableCell><Badge>{item.source}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{dateLabel(item.created_at)}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" className="min-h-8 px-2" disabled={revoking === item.id} onClick={() => revoke(item.id)}>{revoking === item.id ? "Restoring…" : "Allow again"}</Button></TableCell>
                  </TableRow>
                ))}</tbody>
              </Table>
            )}
          </CardSection>
        </>
      )}
    </div>
  );
}
